import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import './App.css';

const PASS = '059805';
const FROM_EMAIL = 'jobs@eshaqjob.store';

const LOGO = () => (
  <svg width="38" height="38" viewBox="0 0 38 38" fill="none">
    <rect width="38" height="38" rx="10" fill="#1a1a2e"/>
    <text x="7" y="26" fontSize="20" fontFamily="Arial" fontWeight="bold" fill="#e8b923">و</text>
    <rect x="22" y="10" width="10" height="2.5" rx="1.2" fill="#e8b923"/>
    <rect x="22" y="15" width="8" height="2.5" rx="1.2" fill="#e8b923" opacity="0.7"/>
    <rect x="22" y="20" width="10" height="2.5" rx="1.2" fill="#e8b923" opacity="0.5"/>
  </svg>
);

function LoginScreen({ onLogin }) {
  const [pass, setPass] = useState('');
  const [err, setErr] = useState('');
  const handle = () => { if (pass !== PASS) { setErr('كلمة المرور غير صحيحة'); return; } onLogin(); };
  return (
    <div className="login-wrap">
      <div className="login-card">
        <div className="login-logo"><LOGO /></div>
        <h1 className="login-title">وظيفتنا</h1>
        <p className="login-sub">مرسل طلبات التوظيف الذكي</p>
        <div className="field-group">
          <label>كلمة المرور</label>
          <input type="password" placeholder="أدخل كلمة المرور" value={pass}
            onChange={e => { setPass(e.target.value); setErr(''); }}
            onKeyDown={e => e.key === 'Enter' && handle()} className="inp" />
          {err && <p className="err-msg">{err}</p>}
        </div>
        <button className="btn-primary full" onClick={handle}>دخول</button>
      </div>
    </div>
  );
}

const TABS = ['الفلتر', 'الرسالة', 'الإرسال', 'السجلات', 'التقارير'];

export default function App() {
  const [loggedIn, setLoggedIn] = useState(() => localStorage.getItem('wazifatna_auth') === '1');
  const [activeTab, setActiveTab] = useState(0);

  // Excel
  const [sheets, setSheets] = useState({});
  const [selectedCities, setSelectedCities] = useState([]);
  const [qtyMode, setQtyMode] = useState('all');
  const [qtyPreset, setQtyPreset] = useState(100);
  const [qtyCustom, setQtyCustom] = useState('');
  const [excelFileName, setExcelFileName] = useState('');
  const [delaySeconds, setDelaySeconds] = useState(5);

  // CV
  const [cvFile, setCvFile] = useState(null);
  const [cvB64, setCvB64] = useState(null);

  // Message
  const [senderName, setSenderName] = useState('وظيفتنا');
  const [clientName, setClientName] = useState('');
  const [subject, setSubject] = useState('طلب توظيف — {{CompanyName}}');
  const [body, setBody] = useState('السادة المسؤولين في {{CompanyName}}،\n\nأتقدم بطلبي للانضمام إلى فريقكم وأرفق سيرتي الذاتية للاطلاع عليها.\n\nشكراً لكم،\nفريق وظيفتنا');

  // Send
  const [sending, setSending] = useState(false);
  const [paused, setPaused] = useState(false);
  const [stopped, setStopped] = useState(false);
  const [sendLog, setSendLog] = useState(() => {
    try { return JSON.parse(localStorage.getItem('wazifatna_log') || '[]'); } catch { return []; }
  });
  const [sendIdx, setSendIdx] = useState(() => {
    try { return JSON.parse(localStorage.getItem('wazifatna_idx') || '0'); } catch { return 0; }
  });
  const [elapsed, setElapsed] = useState(0);

  const [archive, setArchive] = useState([]);
  const [viewingArchive, setViewingArchive] = useState(null);
  const [archiveLoading, setArchiveLoading] = useState(false);

  const pauseRef = useRef(false);
  const stopRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (sending && !paused) { timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000); }
    else { clearInterval(timerRef.current); }
    return () => clearInterval(timerRef.current);
  }, [sending, paused]);

  // حفظ السجل في localStorage تلقائياً
  useEffect(() => {
    try {
      localStorage.setItem('wazifatna_log', JSON.stringify(sendLog));
      localStorage.setItem('wazifatna_idx', JSON.stringify(sendIdx));
    } catch {}
  }, [sendLog, sendIdx]);

  const toggleCity = city => setSelectedCities(prev => prev.includes(city) ? prev.filter(c => c !== city) : [...prev, city]);

  // ─── تحميل الأرشيف من السحابة ───
  const loadArchive = async () => {
    setArchiveLoading(true);
    try {
      const r = await fetch('/api/archive', { cache: 'no-store' });
      const data = await r.json();
      setArchive(Array.isArray(data) ? data : []);
    } catch {}
    setArchiveLoading(false);
  };

  useEffect(() => { loadArchive(); }, []);

  const getTargetList = useCallback(() => {
    const getEmail = r => String(r.Email || r.email || r['الإيميل'] || r['البريد'] || r['البريد الإلكتروني'] || '').trim();

    // إذا ما في مدن محددة — خذ الكل
    if (selectedCities.length === 0) {
      const all = Object.values(sheets).flat().filter(r => getEmail(r));
      if (qtyMode === 'all') return all;
      if (qtyMode === 'preset') return all.slice(0, qtyPreset);
      const n = parseInt(qtyCustom); return all.slice(0, isNaN(n) ? all.length : n);
    }

    // توزيع متساوي بين المدن المختارة
    const limit = qtyMode === 'all' ? Infinity : qtyMode === 'preset' ? qtyPreset : (parseInt(qtyCustom) || Infinity);
    const perCity = limit === Infinity ? Infinity : Math.ceil(limit / selectedCities.length);

    // خذ من كل مدينة حصتها بالتساوي
    const cityBuckets = selectedCities.map(city => {
      const rows = (sheets[city] || []).filter(r => getEmail(r));
      return perCity === Infinity ? rows : rows.slice(0, perCity);
    });

    // دمج بطريقة round-robin (صف من كل مدينة بالتناوب)
    const merged = [];
    const maxLen = Math.max(...cityBuckets.map(b => b.length));
    for (let i = 0; i < maxLen; i++) {
      for (const bucket of cityBuckets) {
        if (i < bucket.length) merged.push(bucket[i]);
      }
    }

    return limit === Infinity ? merged : merged.slice(0, limit);
  }, [sheets, selectedCities, qtyMode, qtyPreset, qtyCustom]);

  const targetList = getTargetList();

  const handleExcel = e => {
    const file = e.target.files[0]; if (!file) return;
    setExcelFileName(file.name);
    const reader = new FileReader();
    reader.onload = ev => {
      const wb = XLSX.read(ev.target.result, { type: 'array' });
      const result = {};
      wb.SheetNames.forEach(name => { result[name] = XLSX.utils.sheet_to_json(wb.Sheets[name]); });
      setSheets(result); setSelectedCities([]);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleCV = e => {
    const file = e.target.files[0]; if (!file) return;
    if (file.size > 3.5 * 1024 * 1024) {
      alert('حجم الملف كبير جداً! الحد الأقصى 3.5MB\nحجم ملفك: ' + (file.size / 1024 / 1024).toFixed(1) + 'MB\nقلّص السيرة الذاتية وحاول مرة ثانية.');
      return;
    }
    setCvFile(file);
    const reader = new FileReader();
    reader.onload = ev => setCvB64(ev.target.result.split(',')[1]);
    reader.readAsDataURL(file);
  };

  const fill = (tpl, row) => tpl
    .replace(/{{CompanyName}}/g, row.CompanyName || row.Company || row['اسم الشركة'] || row['الشركة'] || 'الشركة')
    .replace(/{{ContactName}}/g, row.ContactName || row.Name || row['الاسم'] || 'مسؤول التوظيف')
    .replace(/{{City}}/g, row.City || row['المدينة'] || '');

  const getEmail = row => String(row.Email || row.email || row['الإيميل'] || row['البريد'] || row['البريد الإلكتروني'] || '').trim();

  const startSend = async () => {
    if (!Object.keys(sheets).length) { alert('ارفع ملف Excel أولاً'); return; }
    if (!targetList.length) { alert('لا يوجد إيميلات'); return; }
    if (!body.trim()) { alert('اكتب نص الرسالة أولاً'); return; }

    // أرشفة السجل القديم قبل البدء
    setSending(true); setPaused(false); setStopped(false);
    setSendIdx(0); setElapsed(0);
    setSendLog(prev => {
      if (prev.length > 0) {
        try {
          const archived = JSON.parse(localStorage.getItem('wazifatna_archive') || '[]');
          archived.unshift({ date: new Date().toLocaleString('ar-SA'), log: prev });
          if (archived.length > 10) archived.pop(); // احتفظ بآخر 10 جلسات
          fetch('/api/archive', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'add', entry: { date: new Date().toLocaleString('ar-SA'), log: prev } })
          }).then(() => loadArchive()).catch(() => {});
        } catch {}
      }
      return [];
    });
    pauseRef.current = false; stopRef.current = false;

    for (let i = 0; i < targetList.length; i++) {
      if (stopRef.current) break;
      while (pauseRef.current) { await new Promise(r => setTimeout(r, 500)); }
      if (stopRef.current) break;

      setSendIdx(i + 1);
      const row = targetList[i];
      const to = getEmail(row);
      if (!to) continue;

      try {
        const payload = {
          to,
          subject: fill(subject, row),
          htmlContent: fill(body, row).replace(/\n/g, '<br>'),
          textContent: fill(body, row),
          fromName: senderName || 'وظيفتنا',
          fromEmail: FROM_EMAIL,
        };
        if (cvB64 && cvFile) payload.attachment = [{ content: cvB64, name: cvFile.name }];

        const res = await fetch('/api/send', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const ok = res.ok;
        const data = await res.json().catch(() => ({}));

        setSendLog(prev => [...prev, {
          email: to,
          company: row.CompanyName || row.Company || row['الشركة'] || '—',
          status: ok ? 'sent' : 'failed',
          msgId: data.messageId || '',
          time: new Date().toLocaleTimeString('ar-SA'),
        }]);
      } catch {
        setSendLog(prev => [...prev, { email: to, company: row['الشركة'] || '—', status: 'failed', time: new Date().toLocaleTimeString('ar-SA') }]);
      }

      if (i < targetList.length - 1 && !stopRef.current) {
        await new Promise(r => setTimeout(r, delaySeconds * 1000));
      }
    }
    setSending(false); setPaused(false);
  };

  // ─── تحديث إحصائيات سجل محفوظ ───
  const fetchStatsForArchive = async (archiveIndex, log) => {
    const updated = await Promise.all(
      log.map(async l => {
        if (!l.msgId || l.status === 'failed') return l;
        try {
          const r = await fetch('/api/stats', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ messageId: l.msgId }),
          });
          const d = await r.json();
          return { ...l, delivered: d.delivered, opened: d.opened, clicked: d.clicked };
        } catch { return l; }
      })
    );
    // حفظ التحديث في الأرشيف
    const updatedEntry = { ...archive[archiveIndex], log: updated };
    await fetch('/api/archive', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'delete', index: archiveIndex }),
    });
    await fetch('/api/archive', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'add', entry: updatedEntry }),
    });
    alert('✅ تم تحديث الإحصائيات!');
    loadArchive();
  };

  // ─── حفظ الجلسة الحالية في الأرشيف ───
  const saveToArchive = async () => {
    if (!sendLog.length) { alert('لا يوجد سجل للحفظ'); return; }
    try {
      const entry = {
        date: new Date().toLocaleString('ar-SA'),
        clientName: clientName.trim() || 'بدون اسم',
        log: sendLog,
        sent: sendLog.filter(l => l.status === 'sent').length,
        total: sendLog.length,
      };
      const r = await fetch('/api/archive', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'add', entry }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok) {
        alert('✅ تم الحفظ بنجاح! عدد السجلات: ' + (d.count || ''));
        loadArchive();
      } else {
        alert('❌ فشل الحفظ: ' + (d.error || 'خطأ غير معروف'));
      }
    } catch (e) {
      alert('❌ خطأ في الاتصال: ' + e.message);
    }
  };

  const pauseSend = () => { pauseRef.current = true; setPaused(true); };
  const resumeSend = () => { pauseRef.current = false; setPaused(false); };
  const stopSend = () => { stopRef.current = true; pauseRef.current = false; setSending(false); setPaused(false); setStopped(true); };

  const fetchStats = async () => {
    const updated = await Promise.all(
      sendLog.map(async l => {
        if (!l.msgId || l.status === 'failed') return l;
        try {
          const r = await fetch('/api/stats', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ messageId: l.msgId }) });
          const d = await r.json();
          return { ...l, delivered: d.delivered, opened: d.opened, clicked: d.clicked };
        } catch { return l; }
      })
    );
    setSendLog(updated);
  };

  const exportExcel = () => {
    const rows = sendLog.map(l => ({
      'الإيميل': l.email, 'الشركة': l.company,
      'الإرسال': l.status === 'sent' ? 'تم' : 'فشل',
      'الوصول': l.delivered ? 'وصل' : l.status === 'sent' ? 'لم يتحقق' : '—',
      'الفتح': l.opened ? 'فُتح' : l.status === 'sent' ? 'لم يُفتح' : '—',
      'الوقت': l.time,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'نتائج الإرسال');
    XLSX.writeFile(wb, `وظيفتنا-نتائج-${new Date().toLocaleDateString('ar-SA').replace(/\//g, '-')}.xlsx`);
  };

  const fmt = s => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  const sent = sendLog.filter(l => l.status === 'sent').length;
  const failed = sendLog.filter(l => l.status === 'failed').length;

  return !loggedIn ? <LoginScreen onLogin={() => { setLoggedIn(true); localStorage.setItem('wazifatna_auth', '1'); }} /> : (
    <div className="app" dir="rtl">
      <header className="header">
        <div className="header-left"><LOGO />
          <div><span className="header-title">وظيفتنا</span><span className="header-sub">مرسل طلبات التوظيف</span></div>
        </div>
        <button className="btn-ghost logout" onClick={() => { setLoggedIn(false); localStorage.removeItem('wazifatna_auth'); }}>خروج</button>
      </header>

      <nav className="tabs">
        {TABS.map((t, i) => (
          <button key={i} className={`tab ${activeTab === i ? 'active' : ''}`} onClick={() => setActiveTab(i)}>{t}</button>
        ))}
      </nav>

      <main className="main">

        {activeTab === 0 && (
          <div className="tab-content">
            <div className="card">
              <h3 className="card-title">رفع ملف الإيميلات</h3>
              <label className="upload-box">
                <input type="file" accept=".xlsx,.xls,.csv" onChange={handleExcel} hidden />
                <span className="upload-icon">📂</span>
                <span className="upload-text">{excelFileName || 'اضغط لرفع ملف Excel'}</span>
                {excelFileName && <span className="upload-count">{Object.values(sheets).flat().length} سجل</span>}
              </label>
            </div>

            {Object.keys(sheets).length > 0 && (
              <div className="card">
                <h3 className="card-title">اختر المدن <span style={{fontSize:12,color:'var(--muted)',fontWeight:400}}>(يمكنك اختيار أكثر من مدينة)</span></h3>
                <div className="city-grid">
                  <button className={`city-btn ${selectedCities.length === 0 ? 'active' : ''}`} onClick={() => setSelectedCities([])}>
                    الكل <span className="city-count">{Object.values(sheets).flat().length}</span>
                  </button>
                  {Object.keys(sheets).map(city => (
                    <button key={city} className={`city-btn ${selectedCities.includes(city) ? 'active' : ''}`} onClick={() => toggleCity(city)}>
                      {selectedCities.includes(city) && <span style={{marginLeft:4}}>✓</span>}
                      {city} <span className="city-count">{sheets[city].length}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {Object.keys(sheets).length > 0 && (
              <div className="card">
                <h3 className="card-title">كمية الإرسال</h3>
                <div className="qty-grid">
                  <button className={`qty-btn ${qtyMode === 'all' ? 'active' : ''}`} onClick={() => setQtyMode('all')}>
                    الكل <span className="qty-sub">({Object.values(sheets).flat().length})</span>
                  </button>
                  {[100,200,300,400,500,600,700,800,900].map(n => (
                    <button key={n} className={`qty-btn ${qtyMode === 'preset' && qtyPreset === n ? 'active' : ''}`}
                      onClick={() => { setQtyMode('preset'); setQtyPreset(n); }}>{n}</button>
                  ))}
                  <button className={`qty-btn ${qtyMode === 'custom' ? 'active' : ''}`} onClick={() => setQtyMode('custom')}>تحديد يدوي</button>
                </div>
                {qtyMode === 'custom' && (
                  <input type="number" className="inp" placeholder="أدخل العدد" value={qtyCustom}
                    onChange={e => setQtyCustom(e.target.value)} style={{marginTop:12,maxWidth:180}} />
                )}
                <p className="count-summary">سيتم الإرسال لـ <strong>{targetList.length}</strong> إيميل</p>
              </div>
            )}

            {Object.keys(sheets).length > 0 && (
              <div className="card">
                <h3 className="card-title">التأخير بين الإيميلات</h3>
                <div style={{display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
                  <div style={{flex:1,minWidth:200}}>
                    <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                      <span style={{fontSize:13,color:'var(--muted)'}}>التأخير</span>
                      <strong>{delaySeconds} ثانية</strong>
                    </div>
                    <input type="range" min="1" max="60" value={delaySeconds}
                      onChange={e => setDelaySeconds(Number(e.target.value))}
                      style={{width:'100%',accentColor:'var(--gold)'}} />
                  </div>
                  <div style={{background:'var(--bg)',borderRadius:10,padding:'12px 20px',textAlign:'center',minWidth:140}}>
                    <p style={{fontSize:11,color:'var(--muted)',marginBottom:4}}>الوقت المتوقع</p>
                    <p style={{fontSize:22,fontWeight:700,color:'var(--gold)'}}>
                      {(() => {
                        const t = targetList.length * delaySeconds;
                        const h = Math.floor(t/3600), m = Math.floor((t%3600)/60), s = t%60;
                        if (h > 0) return `${h}س ${m}د`; if (m > 0) return `${m}د ${s}ث`; return `${s}ث`;
                      })()}
                    </p>
                    <p style={{fontSize:11,color:'var(--muted)',marginTop:4}}>{targetList.length} إيميل</p>
                  </div>
                </div>
              </div>
            )}

            <div className="card">
              <h3 className="card-title">رفع السيرة الذاتية (PDF)</h3>
              <label className="upload-box">
                <input type="file" accept=".pdf,.doc,.docx" onChange={handleCV} hidden />
                <span className="upload-icon">📄</span>
                <span className="upload-text">{cvFile ? cvFile.name : 'اضغط لرفع السيرة الذاتية'}</span>
              </label>
            </div>
          </div>
        )}

        {activeTab === 1 && (
          <div className="tab-content">
            <div className="card">
              <h3 className="card-title">اسم المرسل</h3>
              <input className="inp" value={senderName} onChange={e => setSenderName(e.target.value)} placeholder="مثال: محمد العمري" />
              <p className="hint">هذا الاسم يظهر للمستلم كاسم المرسل</p>
            </div>
            <div className="card">
              <h3 className="card-title">عنوان الرسالة</h3>
              <input className="inp" value={subject} onChange={e => setSubject(e.target.value)} placeholder="عنوان الإيميل" />
              <p className="hint">يمكنك استخدام {'{{CompanyName}}'} لإدراج اسم الشركة</p>
            </div>
            <div className="card">
              <h3 className="card-title">نص الرسالة</h3>
              <div className="vars-row">
                {['{{CompanyName}}','{{ContactName}}','{{City}}'].map(v => (
                  <button key={v} className="var-chip" onClick={() => setBody(b => b + v)}>{v}</button>
                ))}
              </div>
              <textarea className="textarea" rows={10} value={body} onChange={e => setBody(e.target.value)} />
            </div>
            {targetList.length > 0 && (
              <div className="card">
                <h3 className="card-title">معاينة</h3>
                <div className="preview-box">
                  <p><strong>العنوان:</strong> {fill(subject, targetList[0])}</p>
                  <hr/>
                  <pre className="preview-body">{fill(body, targetList[0])}</pre>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 2 && (
          <div className="tab-content">
            {/* Client name */}
            <div className="card">
              <h3 className="card-title">اسم العميل</h3>
              <input className="inp" value={clientName} onChange={e => setClientName(e.target.value)} placeholder="مثال: علي العمري" />
              <p className="hint">يظهر في السجلات لتمييز كل جلسة</p>
            </div>

            <div className="stats-grid">
              <div className="stat-card"><span className="stat-label">المستهدف</span><span className="stat-val">{targetList.length}</span></div>
              <div className="stat-card"><span className="stat-label">تم الإرسال</span><span className="stat-val green">{sent}</span></div>
              <div className="stat-card"><span className="stat-label">فشل</span><span className="stat-val red">{failed}</span></div>
              <div className="stat-card"><span className="stat-label">الوقت المنقضي</span><span className="stat-val">{fmt(elapsed)}</span></div>
              <div className="stat-card" style={{gridColumn:'span 2'}}>
                <span className="stat-label">الوقت المتبقي</span>
                <span className="stat-val" style={{fontSize:20}}>
                  {sending && sendIdx > 0 ? (() => {
                    const rem = (targetList.length - sendIdx) * delaySeconds;
                    const h = Math.floor(rem/3600), m = Math.floor((rem%3600)/60), s = rem%60;
                    if (h>0) return `${h}س ${m}د`; if (m>0) return `${m}د ${s}ث`; return `${s}ث`;
                  })() : '—'}
                </span>
              </div>
            </div>

            {(sending || stopped || sendLog.length > 0) && (
              <div className="card">
                <div className="progress-header">
                  <span>{targetList.length} / {sendIdx}</span>
                  <span className={`status-badge ${sending && !paused ? 'running' : paused ? 'paused' : stopped ? 'stopped' : 'done'}`}>
                    {sending && !paused ? 'جارٍ الإرسال' : paused ? 'متوقف مؤقتاً' : stopped ? 'تم الإيقاف' : 'اكتمل'}
                  </span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{width:`${targetList.length ? (sendIdx/targetList.length)*100 : 0}%`}} />
                </div>
              </div>
            )}

            <div className="controls-row">
              {!sending ? (
                <button className="btn-primary" onClick={startSend} disabled={!targetList.length}>ابدأ الإرسال</button>
              ) : (
                <>
                  {!paused ? <button className="btn-pause" onClick={pauseSend}>⏸ إيقاف مؤقت</button>
                    : <button className="btn-resume" onClick={resumeSend}>▶ استكمال</button>}
                  <button className="btn-stop" onClick={stopSend}>⏹ إيقاف نهائي</button>
                </>
              )}
              {sendLog.length > 0 && <button className="btn-export" onClick={exportExcel}>⬇ تصدير Excel</button>}
              {sendLog.length > 0 && !sending && <button className="btn-stats" onClick={fetchStats}>🔄 إحصائيات</button>}
              {sendLog.length > 0 && !sending && <button className="btn-resume" onClick={saveToArchive}>💾 حفظ في السجلات</button>}
              {sendLog.length > 0 && !sending && (
                <button className="btn-stop" onClick={() => {
                  if (window.confirm('هل تريد مسح سجل الإرسال الحالي؟\nتأكد من الحفظ أولاً إذا أردت الاحتفاظ به.')) {
                    setSendLog([]); setSendIdx(0);
                    localStorage.removeItem('wazifatna_log');
                    localStorage.removeItem('wazifatna_idx');
                    alert('✅ تم مسح السجل');
                  }
                }}>🗑 مسح السجل</button>
              )}
            </div>

            {sendLog.length > 0 && (
              <div className="card">
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
                  <h3 className="card-title" style={{margin:0}}>سجل الإرسال</h3>
                  <span style={{fontSize:12,color:'var(--muted)'}}>✓ أُرسل &nbsp; 📬 وصل &nbsp; 👁 فُتح</span>
                </div>
                <div className="log-list">
                  {[...sendLog].reverse().map((l, i) => (
                    <div key={i} className={`log-item ${l.status}`}>
                      <span className={`dot dot-${l.status}`} />
                      <span className="log-email">{l.email}</span>
                      <span className="log-company">{l.company}</span>
                      <span className="log-track">
                        {l.status === 'sent' && <>
                          <span style={{color:'var(--green)'}}>✓</span>
                          <span>{l.delivered !== undefined ? (l.delivered ? ' 📬' : ' 📭') : ' …'}</span>
                          <span>{l.opened !== undefined ? (l.opened ? ' 👁' : ' —') : ' …'}</span>
                        </>}
                        {l.status === 'failed' && <span style={{color:'var(--red)'}}>✗</span>}
                      </span>
                      <span className="log-time">{l.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ TAB 3 — السجلات ══ */}
        {activeTab === 3 && (
          <div className="tab-content">
            <div className="card">
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
                <h3 className="card-title" style={{margin:0}}>السجلات السابقة ☁️</h3>
                <div style={{display:'flex',gap:8}}>
                  {viewingArchive && (
                    <button className="btn-ghost" style={{color:'var(--muted)',fontSize:13}} onClick={() => setViewingArchive(null)}>
                      ← رجوع
                    </button>
                  )}
                  <button className="btn-stats" style={{padding:'6px 14px',fontSize:12}} onClick={loadArchive}>
                    {archiveLoading ? '...' : '🔄 تحديث'}
                  </button>
                </div>
              </div>

              {/* قائمة الجلسات */}
              {!viewingArchive && (
                archive.length === 0 ? (
                  <p className="empty-msg">لا يوجد سجلات سابقة بعد</p>
                ) : (
                  <div style={{display:'flex',flexDirection:'column',gap:10}}>
                    {archive.map((a, i) => (
                      <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'14px 16px',background:'var(--bg)',borderRadius:10,border:'1px solid var(--border)'}}>
                        <div>
                          <p style={{fontWeight:700,fontSize:15,marginBottom:2,color:'var(--text)'}}>{a.clientName || 'بدون اسم'}</p>
                        <p style={{fontSize:12,color:'var(--muted)',marginBottom:3}}>{a.date}</p>
                          <p style={{fontSize:12,color:'var(--muted)'}}>
                            {a.log.length} إيميل &nbsp;|&nbsp;
                            <span style={{color:'var(--green)'}}>✓ {a.log.filter(l=>l.status==='sent').length} تم</span>
                            &nbsp;|&nbsp;
                            <span style={{color:'var(--gold)'}}>👁 {a.log.filter(l=>l.opened).length} فُتح</span>
                          </p>
                        </div>
                        <div style={{display:'flex',gap:8}}>
                          <button className="btn-stats" style={{padding:'8px 14px',fontSize:12}} onClick={() => setViewingArchive(a)}>عرض</button>
                          <button className="btn-resume" style={{padding:'8px 14px',fontSize:12}} onClick={() => fetchStatsForArchive(i, a.log)}>🔄</button>
                          <button className="btn-export" style={{padding:'8px 14px',fontSize:12}} onClick={() => {
                            const rows = a.log.map(l => ({
                              'الإيميل': l.email, 'الشركة': l.company,
                              'الإرسال': l.status === 'sent' ? 'تم' : 'فشل',
                              'الوصول': l.delivered ? 'وصل' : 'لم يتحقق',
                              'الفتح': l.opened ? 'فُتح' : 'لم يُفتح',
                              'الوقت': l.time,
                            }));
                            const ws = XLSX.utils.json_to_sheet(rows);
                            const wb = XLSX.utils.book_new();
                            XLSX.utils.book_append_sheet(wb, ws, 'نتائج');
                            XLSX.writeFile(wb, 'وظيفتنا-' + a.date.replace(/[/:]/g,'-') + '.xlsx');
                          }}>⬇ Excel</button>
                          <button className="btn-stop" style={{padding:'8px 14px',fontSize:12}} onClick={async () => {
                            if (!window.confirm('هل تريد حذف هذا السجل نهائياً؟')) return;
                            const r = await fetch('/api/archive', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ action: 'delete', index: i })
                            });
                            if (r.ok) {
                              alert('✅ تم الحذف بنجاح');
                              loadArchive();
                            } else {
                              alert('❌ فشل الحذف');
                            }
                          }}>🗑</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}

              {/* عرض سجل محدد */}
              {viewingArchive && (
                <div>
                  <p style={{fontSize:13,color:'var(--muted)',marginBottom:12}}>{viewingArchive.log.length} إيميل — {viewingArchive.date}</p>
                  <div className="log-list">
                    {[...viewingArchive.log].reverse().map((l, i) => (
                      <div key={i} className={"log-item " + l.status}>
                        <span className={"dot dot-" + l.status}/>
                        <span className="log-email">{l.email}</span>
                        <span className="log-company">{l.company}</span>
                        <span className="log-track">
                          {l.status==='sent' && <>
                            <span style={{color:'var(--green)'}}>✓</span>
                            <span>{l.delivered!==undefined?(l.delivered?' 📬':' 📭'):' …'}</span>
                            <span>{l.opened!==undefined?(l.opened?' 👁':' —'):' …'}</span>
                          </>}
                          {l.status==='failed' && <span style={{color:'var(--red)'}}>✗</span>}
                        </span>
                        <span className="log-time">{l.time}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 4 && (
          <div className="tab-content">
            <div className="card">
              <h3 className="card-title">ملخص الجلسة</h3>
              <div className="report-grid">
                <div className="report-item"><span className="report-icon">📨</span><span className="report-num">{sendLog.length}</span><span className="report-lbl">إجمالي المحاولات</span></div>
                <div className="report-item"><span className="report-icon">✅</span><span className="report-num green">{sent}</span><span className="report-lbl">تم الإرسال</span></div>
                <div className="report-item"><span className="report-icon">❌</span><span className="report-num red">{failed}</span><span className="report-lbl">فشل</span></div>
                <div className="report-item"><span className="report-icon">⏱</span><span className="report-num">{fmt(elapsed)}</span><span className="report-lbl">وقت الجلسة</span></div>
                <div className="report-item"><span className="report-icon">📊</span><span className="report-num">{sent ? Math.round((sent/sendLog.length)*100) : 0}%</span><span className="report-lbl">معدل النجاح</span></div>
                <div className="report-item"><span className="report-icon">👁</span><span className="report-num">{sendLog.filter(l=>l.opened).length}</span><span className="report-lbl">فتح الإيميل</span></div>
              </div>
              {sendLog.length > 0 && <button className="btn-export full" onClick={exportExcel} style={{marginTop:20}}>⬇ تصدير Excel</button>}
              {!sendLog.length && <p className="empty-msg">لا يوجد بيانات — ابدأ الإرسال أولاً</p>}
            </div>
          </div>
        )}

      </main>
    </div>
  );
}
